import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  FileText,
  Upload,
  Eye,
  AlertCircle,
  Calendar,
  Clock,
  User as UserIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";

import {
  useAcceptWeeklyReport,
  useMarkWeeklyReportRead,
  useRejectWeeklyReport,
  useSubmitWeeklyReport,
  useWeeklyReport,
} from "../hooks/useWeeklyReports";
import {
  WEEKLY_REPORT_STATUS_LABELS,
  type WeeklyReportStatus,
} from "../types";

const statusColors: Record<WeeklyReportStatus, "gray" | "blue" | "green" | "red" | "yellow"> = {
  pending: "yellow",
  submitted: "blue",
  late: "red",
  accepted: "green",
  rejected: "red",
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WeeklyReportDetailPage() {
  const { orderId, reportId } = useParams<{ orderId: string; reportId: string }>();
  const user = useAuthStore((s) => s.user);
  const isGK = user?.role === "director" || user?.role === "coordinator";
  const isContractor = user?.role === "contractor";

  const { data: report, isLoading, isError } = useWeeklyReport(reportId);
  usePageTitle(report ? `Raport ${report.order_number}` : "Raport tygodniowy");

  const acceptMutation = useAcceptWeeklyReport();
  const rejectMutation = useRejectWeeklyReport();
  const markReadMutation = useMarkWeeklyReportRead();
  const submitMutation = useSubmitWeeklyReport();

  const [rejectComment, setRejectComment] = useState("");
  const [showReject, setShowReject] = useState(false);

  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  // Auto-mark as read once for GK when the report is unread + submitted.
  const didMarkRef = useRef(false);
  useEffect(() => {
    if (!report || !isGK || didMarkRef.current) return;
    if (report.status === "submitted" && !report.director_read) {
      didMarkRef.current = true;
      markReadMutation.mutate(report.id);
    }
  }, [report, isGK, markReadMutation]);

  if (isLoading) return <PageLoader />;

  if (isError || !report) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <AlertCircle size={32} className="mx-auto text-red-500 mb-2" />
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Nie udało się załadować raportu lub nie masz do niego dostępu.
        </p>
        <Link
          to={isContractor ? "/contractor/weekly-reports" : "/weekly-reports"}
          className="inline-flex items-center gap-1 mt-4 text-sm text-[var(--color-primary)] hover:underline"
        >
          <ArrowLeft size={14} /> Powrót do listy raportów
        </Link>
      </div>
    );
  }

  // If the route param doesn't match the report's actual order, prefer the report's order id.
  const effectiveOrderId = report.order || orderId;
  const orderPath = isContractor
    ? `/contractor/orders/${effectiveOrderId}`
    : `/orders/${effectiveOrderId}`;
  const listPath = isContractor ? "/contractor/weekly-reports" : "/weekly-reports";

  const isUnread = isGK && report.status === "submitted" && !report.director_read;
  const canSubmit = isContractor && (report.status === "pending" || report.status === "late");
  const canReview = isGK && report.status === "submitted";

  const handleSubmit = () => {
    if (filesToUpload.length === 0) return;
    submitMutation.mutate(
      { id: report.id, files: filesToUpload },
      {
        onSuccess: () => {
          setFilesToUpload([]);
        },
      },
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      {/* Back link */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <Link
          to={listPath}
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          <ArrowLeft size={14} /> Powrót do listy raportów
        </Link>
        <Link
          to={orderPath}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
        >
          <FileText size={14} /> Przejdź do zamówienia
        </Link>
      </div>

      {/* Header */}
      <div className="mb-5">
        <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug">
          Raport tygodniowy
          <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-medium text-gray-500 dark:text-gray-400">
            {report.order_number}
          </span>
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge color={statusColors[report.status]}>
            {WEEKLY_REPORT_STATUS_LABELS[report.status]}
          </Badge>
          {isUnread && <Badge color="blue">Nowy</Badge>}
        </div>
      </div>

      {/* Metadata card */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
          <div className="flex items-start gap-2">
            <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Okres
              </div>
              <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">
                {formatDate(report.period_start)} – {formatDate(report.period_end)}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Clock size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Termin
              </div>
              <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">
                {formatDate(report.due_date)}
              </div>
            </div>
          </div>

          {report.submitted_at && (
            <div className="flex items-start gap-2">
              <Upload size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Złożony
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">
                  {formatDateTime(report.submitted_at)}
                </div>
              </div>
            </div>
          )}

          {report.reviewed_at && (
            <div className="flex items-start gap-2">
              <UserIcon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Sprawdzony
                </div>
                <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5">
                  {formatDateTime(report.reviewed_at)}
                  {report.reviewed_by_name && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      ({report.reviewed_by_name})
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {report.director_comment && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
              Komentarz kierownika
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
              {report.director_comment}
            </p>
          </div>
        )}
      </div>

      {/* Files */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
          <FileText size={15} />
          Załączniki
          <span className="text-xs text-gray-400">({report.files.length})</span>
        </h2>
        {report.files.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak załączników.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {report.files.map((f) => (
              <li key={f.id}>
                <a
                  href={f.file_url ?? f.file}
                  download={f.original_name}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 p-2 rounded-md border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors group"
                >
                  <FileText
                    size={14}
                    className="text-gray-400 group-hover:text-[var(--color-primary)] flex-shrink-0"
                  />
                  <span className="text-sm text-[var(--color-primary)] truncate" title={f.original_name}>
                    {f.original_name}
                  </span>
                  {f.uploaded_by_name && (
                    <span className="ml-auto text-[10px] text-gray-400 hidden sm:inline">
                      {f.uploaded_by_name}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Contractor: submit files */}
      {canSubmit && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 mb-4">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <Upload size={15} />
            Złóż raport
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Wybierz pliki, które chcesz dołączyć do raportu, a następnie wyślij.
          </p>
          <FileDropZone
            multiple
            files={filesToUpload}
            onFilesChange={setFilesToUpload}
          />
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={filesToUpload.length === 0 || submitMutation.isPending}
            >
              <Upload size={13} /> Złóż raport
            </Button>
          </div>
        </div>
      )}

      {/* GK: accept / reject */}
      {canReview && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 mb-4">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <Eye size={15} />
            Przegląd raportu
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              size="sm"
              onClick={() => acceptMutation.mutate(report.id)}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              className="w-full sm:w-auto"
            >
              <CheckCircle2 size={13} /> Zaakceptuj raport
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowReject(true);
                setRejectComment("");
              }}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              className="w-full sm:w-auto"
            >
              <XCircle size={13} /> Odrzuć raport
            </Button>
          </div>

          {showReject && (
            <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-3">
              <label className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Komentarz odrzucenia (wymagany)
              </label>
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 min-h-[80px]"
                rows={3}
                placeholder="Wyjaśnij, dlaczego raport jest odrzucany…"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
              />
              <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowReject(false)}
                  disabled={rejectMutation.isPending}
                  className="w-full sm:w-auto"
                >
                  Anuluj
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={!rejectComment.trim() || rejectMutation.isPending}
                  onClick={() =>
                    rejectMutation.mutate(
                      { id: report.id, comment: rejectComment.trim() },
                      {
                        onSuccess: () => {
                          setShowReject(false);
                          setRejectComment("");
                        },
                      },
                    )
                  }
                  className="w-full sm:w-auto"
                >
                  Potwierdź odrzucenie
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No access fallback */}
      {!isGK && !isContractor && (
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          Brak uprawnień do akcji w tym raporcie.
        </div>
      )}
    </div>
  );
}
